import { useMemo, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuthTokenFromCookies } from "../../../common/utils";
import {
  GLOBAL_HEADER_HEIGHT,
  GLOBAL_HEADER_TRANSITION,
  useGlobalHeaderOffset,
} from "../headerOverlay";

import Button from "../../button/Button";
import NaviMenu from "../../navigation/NaviMenu";
import AccountPopover from "../../account-popover/AccountPopover";
import SoremonHeaderButton from "../../soremon/SoremonHeaderButton";
import { HeaderAudioToggle } from "./GlobalAudioToggle";
import HeaderShortcutHelp from "./HeaderShortcutHelp";
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import { useMessengerScopeSubscribe } from "../../../app/messenger/MessengerSocketContext";
import type { MessengerIncomingEvent } from "../../../types/messengerEvents";
import { isUuid } from "../../../utils/uuid";

interface HeaderStyleProps {
  $glass?: boolean;
  $headerTop?: number;
}

interface MenuItem {
  label: string;
  path: string;
}

interface GlobalHeaderProps {
  isHideTopBar?: boolean;
}

/** /api/me の取得失敗時の再試行間隔（指数バックオフ: 1s → 2s → 4s … 最大 30s） */
const ME_RETRY_BASE_MS = 1000;
const ME_RETRY_MAX_MS = 30000;
/** タスクボード通知を受けて /api/me を取り直す最短間隔 */
const TASK_ACCESS_RECHECK_INTERVAL_MS = 60000;

const errorStatusOf = (error: unknown): unknown =>
  error && typeof error === "object" && "status" in error
    ? (error as { status: unknown }).status
    : undefined;

const Header = styled.div.attrs({
  className: "u-row-between u-border-bottom",
})<HeaderStyleProps>`
  width: 100%;
  height: ${GLOBAL_HEADER_HEIGHT}px;
  /* プロジェクト内（$glass）はグラスモーフィズム: バナー色連動の
     アンビエント背景や、下をくぐるコンテンツの色が透けて反射する */
  ${(props) =>
    props.$glass
      ? `
    background-color: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(16px) saturate(1.6);
    -webkit-backdrop-filter: blur(16px) saturate(1.6);
  `
      : `
    background-color: #fff;
  `}
  padding: 0 16px;
  z-index: 6;
  position: fixed;
  top: ${(props) => (props.$headerTop ? `${props.$headerTop}px` : "0px")};
  transition: top ${GLOBAL_HEADER_TRANSITION};
  left: 0;
  right: 0;
`;

const HeaderLeft = styled.div.attrs({
  className: "u-row u-gap24",
})``;

/* 右端スロット: オーディオトグル → ショートカットヘルプ（?）→ soremon の順に並べる */
const HeaderRight = styled.div.attrs({
  className: "u-row u-gap8",
})``;

const GlobalHeader = ({ isHideTopBar = false }: GlobalHeaderProps) => {
  // 未ログイン（公開ノートなど requireAuth なしページの匿名閲覧）では /me が
  // 必ず 401 になるため、クエリ自体をスキップしてサインイン導線のみ表示する
  const hasAuthToken = !!getAuthTokenFromCookies();
  const { data: currentUser, isLoading, error, refetch } = useGetCurrentUserQuery(
    undefined,
    { skip: !hasAuthToken },
  );
  const location = useLocation();
  const navigate = useNavigate();
  // 下スクロール時は -64px に退避させ、ヘッダーを画面上端の外へ隠す
  const headerTop = useGlobalHeaderOffset() - GLOBAL_HEADER_HEIGHT;
  // プロジェクト内（先頭セグメントが UUID = プロジェクト ID）はグラス化する。
  // slug は UUID 形式を取れないため、この判定でプロジェクトページのみに絞れる。
  const isProjectContext = isUuid(location.pathname.split("/")[1] ?? "");

  // トークンが存在するがユーザー情報がない場合（ログイン直後・一時的な障害）に
  // refetch で回復を試みる。障害中に全タブが即時再試行を繰り返すとサーバーへの
  // 負荷が雪だるま式に増えるため、指数バックオフで間隔を空ける。
  // 401 / 403 はトークン自体が無効なので再試行しない（RequireAuth がサインインへ誘導する）。
  const meRetryCountRef = useRef(0);
  useEffect(() => {
    if (currentUser?.data) {
      meRetryCountRef.current = 0;
      return undefined;
    }
    const token = getAuthTokenFromCookies();
    if (!token || isLoading) return undefined;
    const status = errorStatusOf(error);
    if (status === 401 || status === 403) return undefined;
    const delay = Math.min(
      ME_RETRY_MAX_MS,
      ME_RETRY_BASE_MS * 2 ** meRetryCountRef.current,
    );
    const timer = window.setTimeout(() => {
      meRetryCountRef.current += 1;
      refetch();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [currentUser, isLoading, error, refetch]);

  // 横断タスクボード（/taskboard）を 1 件でも閲覧できるときだけ TASK を出す。
  // レビュアー・スペシャリストのみのユーザーは空のボードに着地しないよう非表示。
  const hasTaskBoardAccess = !!currentUser?.data?.has_task_board_access;

  // タスクボードの共有設定が変わると BE がプロジェクト全メンバーへ
  // task_board.updated を配信する。未共有の間だけ /api/me を取り直し、
  // 共有された直後にリロードなしで TASK を出す。この通知はタスク編集のたびにも
  // 全メンバーへ届くため、取り直しは最短 60 秒に 1 回へ間引き、間引いた分は
  // 窓の終わりに 1 回だけまとめて行う（表示済みのときは取り直さない）。
  const lastAccessRecheckRef = useRef(0);
  const pendingAccessRecheckRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (pendingAccessRecheckRef.current !== null) {
        window.clearTimeout(pendingAccessRecheckRef.current);
      }
    },
    [],
  );
  const handleTaskBoardEvent = useCallback(
    (ev: MessengerIncomingEvent) => {
      if (ev.type !== "task_board.updated" || hasTaskBoardAccess) return;
      if (pendingAccessRecheckRef.current !== null) return;
      const wait =
        lastAccessRecheckRef.current + TASK_ACCESS_RECHECK_INTERVAL_MS - Date.now();
      const recheck = () => {
        pendingAccessRecheckRef.current = null;
        lastAccessRecheckRef.current = Date.now();
        refetch();
      };
      if (wait <= 0) {
        recheck();
      } else {
        pendingAccessRecheckRef.current = window.setTimeout(recheck, wait);
      }
    },
    [hasTaskBoardAccess, refetch],
  );
  useMessengerScopeSubscribe(
    currentUser?.data ? "task_board" : undefined,
    handleTaskBoardEvent,
  );

  // ユーザーロールに基づいてメニュー項目を定義（メモ化）
  const menuItems = useMemo((): MenuItem[] => {
    if (!currentUser?.data) return [];
    const taskItems: MenuItem[] = hasTaskBoardAccess
      ? [{ label: "TASK", path: "/taskboard" }]
      : [];
    switch (currentUser.data.role) {
      case "ADMIN":
        return [
          ...taskItems,
          { label: "PROJECT", path: "/projects" },
          { label: "PAYMENT", path: "/payment" },
          { label: "BALANCE", path: "/console/balance-credit" },
          { label: "ACR", path: "/console/acr" },
          { label: "AI", path: "/console/ai" },
          { label: "DEMO", path: "/console/demo" },
          // GALLERY タグ統合（マスタータグ＋エイリアス）管理
          { label: "TAG", path: "/console/gallery-tags" },
          { label: "ACCOUNT", path: "/account" },
          // プロジェクト管理（集計列付き一覧・削除）。PROJECT（一覧画面）とは別物
          { label: "MANAGE", path: "/console/projects" },
          // 旧 /mileage は SPA ルート未定義で旧 Django に流れていたため
          // /console/* の SPA ページへ移設
          { label: "MILEAGE", path: "/console/mileage" },
          { label: "GITHUB", path: "/console/github" },
          { label: "PAYOUT", path: "/payout" }
        ];
      case "MANAGER":
        return [
          { label: "GALLERY", path: "/gallery" },
          // GALLERY タグ統合（マスタータグ＋エイリアス）管理
          { label: "TAG", path: "/console/gallery-tags" },
          { label: "ACCOUNT", path: "/account" },
          { label: "ROLE", path: "/roles" }
        ];
      case "CREATOR":
      case "CLIENT":
        return [
          ...taskItems,
          { label: "PROJECT", path: "/projects" },
          { label: "GALLERY", path: "/gallery" },
          // 実験的ツール群（PHRASE BLOOM 等）。CREATOR / CLIENT のみに表示
          { label: "TOOL", path: "/tool" }
        ];
      default:
        return [];
    }
  }, [currentUser, hasTaskBoardAccess]);

  // どの項目にも一致しないページ（プロジェクト詳細 /:uuid など）は、TASK が
  // 先頭にあっても従来どおり PROJECT をアクティブにする。
  const defaultActiveLabel = useMemo(() => {
    const matched = menuItems.find(item => item.path && location.pathname.startsWith(item.path));
    const fallback =
      menuItems.find((item) => item.label === "PROJECT") ?? menuItems[0];
    return matched?.label || fallback?.label;
  }, [menuItems, location.pathname]);

  if (isHideTopBar) {
    return null;
  }

  // 未ログイン: 最小限のヘッダーとサインイン導線のみ表示
  if (!hasAuthToken) {
    return (
      <Header $headerTop={headerTop} $glass={isProjectContext}>
        <HeaderLeft />
        <HeaderRight>
          <Button size="small" onClick={() => navigate("/signin")}>
            サインイン
          </Button>
        </HeaderRight>
      </Header>
    );
  }

  // エラーが発生した場合
  if (error) {
    return (
      <Header $headerTop={headerTop} $glass={isProjectContext}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--soremo-text-light)' }}>
          <span>ユーザー情報の取得に失敗しました</span>
          <button 
            onClick={() => refetch()} 
            style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--soremo-border)' }}
          >
            再試行
          </button>
        </div>
        <HeaderRight>
          <HeaderAudioToggle />
          <HeaderShortcutHelp />
        </HeaderRight>
      </Header>
    );
  }

  // ローディング中またはデータがない場合は、最小限のヘッダーを表示
  if (isLoading || !currentUser?.data) {
    return (
      <Header $headerTop={headerTop} $glass={isProjectContext}>
        <HeaderLeft />
        <HeaderRight>
          <HeaderAudioToggle />
          <HeaderShortcutHelp />
        </HeaderRight>
      </Header>
    );
  }

  return (
    <Header $headerTop={headerTop} $glass={isProjectContext}>
      <HeaderLeft>
        <AccountPopover userData={currentUser?.data} />
        <NaviMenu
          items={menuItems}
          defaultActive={defaultActiveLabel}
        />
      </HeaderLeft>
      <HeaderRight>
        <HeaderAudioToggle />
        <HeaderShortcutHelp />
        <SoremonHeaderButton />
      </HeaderRight>
    </Header>
  );
};

export default GlobalHeader;
